'use client';

import type { FC } from 'react';
import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { Spinner, theme } from '@frameio/vapor';
import TEXT from './generated/TEXT/LoadingMoreIndicator';

TEXT.DEFINE({
  /** Accessible label for the spinner shown while loading the next page of results */
  spinnerLabel: 'Next page loading',
});

const crossFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2, ease: theme.transitions.curves.primary },
};

const SpinnerContainer = styled(motion.div)`
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
`;

type LoadingNextPageContainerProps = {
  $loading: boolean;
  $height: number;
};

const LoadingNextPageContainer = styled.div<LoadingNextPageContainerProps>`
  height: ${(p) => (p.$loading ? `${p.$height}px` : 0)};
  opacity: ${(p) => (p.$loading ? 1 : 0)};
  position: relative;
  transition:
    opacity 0.2s ${theme.transitions.eases.primary},
    height 0.2s ${theme.transitions.eases.primary};
  width: 100%;
`;

type LoadingIndicatorProps = {
  loading: boolean;
  height: number;
  delay?: number;
};

export const LoadingMoreIndicator: FC<
  React.PropsWithChildren<LoadingIndicatorProps>
> = ({ loading, height, delay }) => {
  return (
    <LoadingNextPageContainer $height={height} $loading={loading}>
      <AnimatePresence>
        {loading && (
          <SpinnerContainer
            {...crossFade}
            key={`loading${loading}`}
            data-testid="loading-more-indicator"
          >
            <Spinner
              aria-label={TEXT.spinnerLabel}
              delay={delay}
              isLoading={loading}
              size="small"
            />
          </SpinnerContainer>
        )}
      </AnimatePresence>
    </LoadingNextPageContainer>
  );
};
